// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Stack,
    useMediaQuery,
    useTheme,
    FormControl,
    Autocomplete,
    TextField,
    Typography,
    Paper,
} from "@mui/material";
import {
    GridColDef,
    GridPaginationModel,
} from "@mui/x-data-grid-pro";
import { Table, Loading, Button } from "../../../components";
import BaseFilterTableWithAccordion from "../../atoms/BaseFilterTableWithAccordion";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as zod from "zod";
import dayjs from "dayjs";
import { useGetSalesmanBonusList, useCreateSalesmanBonus, useUpdateSalesmanBonus, getSalesmanBonusList as getSalesmanBonusListApi } from "../../../hooks";
import { useSetSnackBar } from "../../../store/Snackbar.state";
import { NoDataIllustration } from "../../../assets/icons";
import { downloadExcel } from "../../../util/downloadExcel";

const currentYear = new Date().getFullYear();
const startYear = 2020;
const years = [];
for (let year = currentYear; year >= startYear; year--) {
    years.push({ label: year.toString(), value: year });
}

const months = [
    { label: "Ocak", value: 1 },
    { label: "Şubat", value: 2 },
    { label: "Mart", value: 3 },
    { label: "Nisan", value: 4 },
    { label: "Mayıs", value: 5 },
    { label: "Haziran", value: 6 },
    { label: "Temmuz", value: 7 },
    { label: "Ağustos", value: 8 },
    { label: "Eylül", value: 9 },
    { label: "Ekim", value: 10 },
    { label: "Kasım", value: 11 },
    { label: "Aralık", value: 12 },
];

const FilterSchema = zod.object({
    year: zod.number({ required_error: "Yıl seçiniz" }).nullable(),
    month: zod.number({ required_error: "Ay seçiniz" }).nullable(),
});

type FilterFormValues = zod.infer<typeof FilterSchema>;

const CustomEditComponent = (props: any) => {
    const { id, field, value: initialValue, api, updateProperty, setSnackbar, setTableData, tableData, isReadonly } = props;
    const [value, setValue] = useState(initialValue);
    const [originalValue, setOriginalValue] = useState(initialValue);

    const handleSave = () => {
        if (value === originalValue) {
            api.stopCellEditMode({ id, field, ignoreModifications: true });
            return;
        }

        const payload = {
            id: id,
            propertyName: field,
            propertyValue: value
        };

        updateProperty(payload, {
            onSuccess: (response: any) => {
                if (response.isSuccess) {
                    setSnackbar({ severity: "success", description: "Güncelleme başarılı", isOpen: true });
                    setOriginalValue(value);
                    api.setEditCellValue({ id, field, value: value });

                    // Update local state
                    setTableData((prev: any[]) => prev.map((item: any) => {
                        if (item.parameterTermId === id) {
                            return { ...item, [field]: value };
                        }
                        return item;
                    }));

                } else {
                    setSnackbar({ severity: "error", description: response.message || "Güncelleme başarısız", isOpen: true });
                    setValue(originalValue);
                }
            },
            onError: () => {
                setSnackbar({ severity: "error", description: "Hata oluştu", isOpen: true });
                setValue(originalValue);
            }
        });

        api.stopCellEditMode({ id, field, ignoreModifications: false });
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSave();
            event.stopPropagation();
        } else if (event.key === 'Escape') {
            setValue(originalValue);
            api.stopCellEditMode({ id, field, ignoreModifications: true });
            event.stopPropagation();
        }
    };

    if (isReadonly) {
        return (
            <span style={{ paddingLeft: 8 }}>
                {initialValue}
            </span>
        );
    }

    return (
        <TextField
            sx={{ paddingLeft: 1 }}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            variant="standard"
            fullWidth
            autoFocus
            InputProps={{ disableUnderline: true }}
        />
    );
};

const CustomNoRowsOverlay = ({ handleCreate, isCreateLoading }: { handleCreate: () => void, isCreateLoading: boolean }) => {
    return (
        <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: '100%', p: 2 }}
        >
            <Paper
                elevation={0}
                variant="outlined"
                sx={{
                    p: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 3,
                    maxWidth: 450,
                    textAlign: 'center',
                    borderRadius: 4,
                    bgcolor: 'background.paper',
                    border: '1px dashed',
                    borderColor: 'divider'
                }}
            >
                <NoDataIllustration style={{ fontSize: 60, width: 150, height: 'auto', opacity: 0.8 }} />
                <Box>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                        Kayıt Bulunamadı
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 350, mx: 'auto' }}>
                        Seçili dönem için henüz bonus hesaplaması yapılmamış. Yeni bonus kayıtları oluşturmak için işlemi başlatın.
                    </Typography>
                </Box>
                <Button
                    text="HESAPLAMAYI BAŞLAT VE OLUŞTUR"
                    variant="contained"
                    color="primary"
                    onClick={handleCreate}
                    disabled={isCreateLoading}
                    sx={{ px: 4, py: 1.5, borderRadius: 2 }}
                />
            </Paper>
        </Stack>
    );
};

interface SalesmanBonusesTableProps {
    filters?: {
        startDate: any;
        endDate: any;
    };
}

export const SalesmanBonusesTable = ({ filters: externalFilters }: SalesmanBonusesTableProps) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
    const setSnackbar = useSetSnackBar();
    const [isFilterAccordionExpanded, setFilterAccordionExpand] = useState(true);
    const [showTable, setShowTable] = useState(false);
    const [shouldRefresh, setShouldRefresh] = useState(false);
    const [isExcelLoading, setIsExcelLoading] = useState(false);

    const { control, handleSubmit, getValues, formState: { errors } } = useForm<FilterFormValues>({
        resolver: zodResolver(FilterSchema),
        defaultValues: {
            year: currentYear,
            month: dayjs().month() + 1,
        }
    });

    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: 25,
    });

    const [filters, setFilters] = useState<FilterFormValues | null>(null);

    const { mutate: getSalesmanBonusListMutation, isLoading: isListLoading, data: listResponse } = useGetSalesmanBonusList();
    const { mutate: createSalesmanBonus, isLoading: isCreateLoading } = useCreateSalesmanBonus();
    const { mutate: updateSalesmanBonus } = useUpdateSalesmanBonus();

    const [tableData, setTableData] = useState<any[]>([]);

    const onSubmit = (data: FilterFormValues) => {
        setFilters(data);
        setFilterAccordionExpand(false);
        setShowTable(true);
        fetchData(data);
    };

    const fetchData = (filterData: any) => {
        const payload = {
            Page: paginationModel.page + 1,
            Size: paginationModel.pageSize,
            IncludePagination: true
        };

        if (externalFilters) {
            payload.StartDate = externalFilters.startDate?.format("YYYY-MM-DD");
            payload.EndDate = externalFilters.endDate?.format("YYYY-MM-DD");
        } else {
            payload.Year = filterData?.year;
            payload.Month = filterData?.month;
        }

        getSalesmanBonusListMutation(payload, {
            onSuccess: (response) => {
                if (response.isSuccess) {
                    setTableData(response.data?.result || []);
                } else {
                    setTableData([]);
                    setSnackbar({
                        severity: "error",
                        description: response.message || "Veri çekilemedi",
                        isOpen: true,
                    });
                }
            },
            onError: () => {
                setTableData([]);
                setSnackbar({
                    severity: "error",
                    description: "Bir hata oluştu",
                    isOpen: true,
                });
            }
        });
    };

    useEffect(() => {
        if (externalFilters) {
            fetchData();
            setShowTable(true);
        } else if (filters) {
            fetchData(filters);
        }
    }, [paginationModel, shouldRefresh, externalFilters?.startDate, externalFilters?.endDate]);

    const handleCreate = () => {
        const values = getValues();
        if (!externalFilters && (!values.year || !values.month)) return;

        const payload = {};
        if (externalFilters) {
            payload.startDate = externalFilters.startDate?.format("YYYY-MM-DD");
            payload.endDate = externalFilters.endDate?.format("YYYY-MM-DD");
        } else {
            payload.year = values.year;
            payload.month = values.month;
        }

        createSalesmanBonus(payload, {
            onSuccess: (response) => {
                if (response.isSuccess) {
                    setSnackbar({
                        severity: "success",
                        description: "Bonuslar başarıyla oluşturuldu.",
                        isOpen: true,
                    });
                    setShouldRefresh(prev => !prev);
                } else {
                    setSnackbar({
                        severity: "error",
                        description: response.message || "Oluşturma başarısız",
                        isOpen: true,
                    });
                }
            },
            onError: () => {
                setSnackbar({
                    severity: "error",
                    description: "Bir hata oluştu",
                    isOpen: true,
                });
            }
        });
    };

    const handleDownloadExcel = async () => {
        const totalItemsCount = listResponse?.data?.totalItems || tableData.length;

        if (!externalFilters && !filters) return;

        setIsExcelLoading(true);
        try {
            const payload = {
                Page: 1,
                Size: totalItemsCount,
                IncludePagination: true
            };

            if (externalFilters) {
                payload.StartDate = externalFilters.startDate?.format("YYYY-MM-DD");
                payload.EndDate = externalFilters.endDate?.format("YYYY-MM-DD");
            } else {
                payload.Year = filters.year;
                payload.Month = filters.month;
            }

            const response = await getSalesmanBonusListApi(payload);

            if (response && response.isSuccess) {
                const mappedData = response.data?.result?.map((item: any) => ({
                    "Ad": item.ad,
                    "Soyad": item.soyad,
                    "Rol": item.userRole,
                    "Bölge": item.territory,
                    "Parametre Adı": item.parameterName,
                    "Oran": item.rate,
                    "Max": item.max,
                    "Hedef": item.goal,
                    "Yıl": item.year,
                    "Ay": item.month,
                    "Kâr": item.profit
                })) || [];

                downloadExcel(mappedData, "Satışçı Bonus Listesi");
            }
        } catch (error) {
            setSnackbar({
                severity: "error",
                description: "Excel indirme sırasında bir hata oluştu",
                isOpen: true,
            });
        } finally {
            setIsExcelLoading(false);
        }
    };

    const columns: GridColDef[] = useMemo(() => [
        { field: "ad", headerName: "Ad", width: 120 },
        { field: "soyad", headerName: "Soyad", width: 120 },
        { field: "userRole", headerName: "Rol", width: 150 },
        { field: "territory", headerName: "Bölge", width: 120 },
        { field: "parameterName", headerName: "Parametre Adı", width: 200 },
        { field: "rate", headerName: "Oran", width: 80 },
        {
            field: "max",
            headerName: "Max",
            width: 100,
            editable: true,
            renderEditCell: (params) => (
                <CustomEditComponent
                    {...params}
                    updateProperty={updateSalesmanBonus}
                    setSnackbar={setSnackbar}
                    setTableData={setTableData}
                    tableData={tableData}
                />
            )
        },
        {
            field: "goal",
            headerName: "Hedef",
            width: 120,
            editable: true,
            renderEditCell: (params) => (
                <CustomEditComponent
                    {...params}
                    updateProperty={updateSalesmanBonus}
                    setSnackbar={setSnackbar}
                    setTableData={setTableData}
                    tableData={tableData}
                />
            )
        },
        { field: "year", headerName: "Yıl", width: 80 },
        { field: "month", headerName: "Ay", width: 80 },
        {
            field: "profit",
            headerName: "Kâr",
            width: 150,
            editable: true,
            isCellEditable: (params) => Number(params.row.parameterId) === 5,
            renderCell: (params) => {
                const isEditable = Number(params.row.parameterId) === 5;
                if (!isEditable) return "Değiştirilemez";
                return params.value ?? 0;
            },
            renderEditCell: (params) => {
                if (Number(params.row.parameterId) !== 5) {
                    return <span />;
                }
                return (
                    <CustomEditComponent
                        {...params}
                        updateProperty={updateSalesmanBonus}
                        setSnackbar={setSnackbar}
                        setTableData={setTableData}
                        tableData={tableData}
                    />
                );
            }
        }
    ], [updateSalesmanBonus, tableData]);

    const isLoading = isListLoading || isCreateLoading || isExcelLoading;

    return (
        <Box sx={{ p: 2 }}>
            {isLoading && <Loading />}
            {!externalFilters && (
                <BaseFilterTableWithAccordion
                    isExpanded={isFilterAccordionExpanded}
                    setExpanded={setFilterAccordionExpand}
                    setTableVisibility={setShowTable}
                    summary="Filtreleme"
                    child={
                        <Stack spacing={2} direction={isDesktop ? "row" : "column"}>
                            <FormControl sx={{ minWidth: 200 }}>
                                <Controller
                                    control={control}
                                    name="year"
                                    render={({ field }) => (
                                        <Autocomplete
                                            options={years}
                                            getOptionLabel={(option) => option.label}
                                            value={years.find(y => y.value === field.value) || null}
                                            onChange={(_, newValue) => field.onChange(newValue?.value || null)}
                                            renderInput={(params) => <TextField {...params} label="Yıl" />}
                                        />
                                    )}
                                />
                            </FormControl>
                            <FormControl sx={{ minWidth: 200 }}>
                                <Controller
                                    control={control}
                                    name="month"
                                    render={({ field }) => (
                                        <Autocomplete
                                            options={months}
                                            getOptionLabel={(option) => option.label}
                                            value={months.find(m => m.value === field.value) || null}
                                            onChange={(_, newValue) => field.onChange(newValue?.value || null)}
                                            renderInput={(params) => <TextField {...params} label="Ay" />}
                                        />
                                    )}
                                />
                            </FormControl>
                            <Button text="LİSTELE" variant="contained" onClick={handleSubmit(onSubmit)} />
                        </Stack>
                    }
                />
            )}

            <Box sx={{ mt: externalFilters ? 0 : 2 }}>
                {showTable && (
                    <Table
                        rows={tableData}
                        editMode="cell"
                        columns={columns}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        rowCount={tableData.length}
                        getRowId={(row) => row.parameterTermId || Math.random()}
                        autoHeight={tableData.length > 0}
                        onSave={handleDownloadExcel}
                        slots={{
                            noRowsOverlay: () => <CustomNoRowsOverlay handleCreate={handleCreate} isCreateLoading={isCreateLoading} />
                        }}
                        sx={{ minHeight: '60vh' }}
                    />
                )}
            </Box>
        </Box>
    );
};
